import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { getBranches, getMyPreferences, updateMyPreferences } from "../api";
import useCurrentUser from "../hooks/useCurrentUser";
import usePrincipalDispatch from "../hooks/usePrincipalDispatch";
import { hasAnyPermission, hasPermission } from "../utils/permissions";
import { NAV_SECTIONS } from "../config/navConfig";
import InlineLoading from "../components/InlineLoading";
import CategoryFilterBar from "../components/CategoryFilterBar";
import { normalizePaginatedResponse } from "../utils/pagination";
import {
  getSelectedBranchId,
  persistSelectedBranchId,
  subscribeSelectedBranch,
} from "../utils/selectedBranch";

const DEFAULT_SHORTCUTS = ["pos", "price-list", "delivery-guides"];

const SHORTCUT_DEFINITIONS = [
  {
    id: "pos",
    label: "Punto de venta",
    sectionId: "pos",
    sectionLabel: "Punto de venta",
    permissions: ["sales.create", "pos.sell"],
    requiresBranch: true,
    path: ({ branchId }) =>
      branchId ? `/branches/${branchId}/purchases/new` : null,
  },
  {
    id: "price-list",
    label: "Lista de precios",
    sectionId: "catalog",
    sectionLabel: "Lista de precios",
    permissions: ["price.read"],
    requiresBranch: true,
    path: ({ branchId }) =>
      branchId ? `/branches/${branchId}/price-list` : null,
  },
  {
    id: "delivery-guides",
    label: "Guias de despacho",
    sectionId: "sales",
    sectionLabel: "Ventas y documentos",
    permissions: [
      "delivery_guide.read",
      "delivery_guide.create",
      "delivery_guide.bulk",
      "delivery_guide.transfer",
    ],
    requiresBranch: true,
    path: ({ branchId }) =>
      branchId ? `/branches/${branchId}/delivery-guides` : null,
  },
];

const buildNavShortcuts = () =>
  NAV_SECTIONS.flatMap((section) =>
    (section.items || []).map((item) => ({
      id: item.id,
      label: item.label,
      sectionId: section.id,
      sectionLabel: section.label,
      permissions: item.permissions || [],
      requireAll: item.requireAll || false,
      requiresBranch: item.requiresBranch || false,
      principalDispatchOnly: item.principalDispatchOnly || false,
      path: item.path,
    }))
  );

const hasShortcutAccess = (user, shortcut) => {
  if (!shortcut?.permissions?.length) return true;
  if (shortcut.requireAll) {
    return shortcut.permissions.every((permission) =>
      hasAnyPermission(user, [permission])
    );
  }
  return hasAnyPermission(user, shortcut.permissions);
};

const HomePage = () => {
  const navigate = useNavigate();
  const { user } = useCurrentUser();
  const [branchId, setBranchId] = useState(getSelectedBranchId);
  const { eligible: canDispatch } = usePrincipalDispatch(branchId, user);
  const [pinnedIds, setPinnedIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [branchModalOpen, setBranchModalOpen] = useState(false);
  const [selectedShortcutCategory, setSelectedShortcutCategory] = useState("all");
  const [branches, setBranches] = useState([]);
  const [branchLoading, setBranchLoading] = useState(false);
  const [branchError, setBranchError] = useState("");
  const canReadBranches =
    hasPermission(user, "branch.read") || hasPermission(user, "branch.access.any");

  const shortcutCatalog = useMemo(() => {
    const navShortcuts = buildNavShortcuts();
    const merged = [...SHORTCUT_DEFINITIONS, ...navShortcuts].reduce((acc, item) => {
      if (!acc.some((entry) => entry.id === item.id)) {
        acc.push(item);
      }
      return acc;
    }, []);
    return merged.filter((item) => hasShortcutAccess(user, item));
  }, [user]);

  const shortcutMap = useMemo(() => {
    const map = new Map();
    shortcutCatalog.forEach((shortcut) => map.set(shortcut.id, shortcut));
    return map;
  }, [shortcutCatalog]);

  const normalizePinned = (ids) => {
    const unique = [];
    ids.forEach((id) => {
      if (shortcutMap.has(id) && !unique.includes(id)) unique.push(id);
    });
    return unique;
  };

  useEffect(() => {
    let mounted = true;
    const loadPreferences = async () => {
      setLoading(true);
      setError("");
      try {
        const preferences = await getMyPreferences();
        const stored = Array.isArray(preferences?.homeShortcuts)
          ? preferences.homeShortcuts
          : [];
        const resolved = stored.length ? stored : DEFAULT_SHORTCUTS;
        if (mounted) {
          setPinnedIds(normalizePinned(resolved));
        }
      } catch (err) {
        console.error(err);
        if (mounted) {
          setPinnedIds(normalizePinned(DEFAULT_SHORTCUTS));
          setError("No se pudieron cargar tus atajos.");
        }
      } finally {
        if (mounted) setLoading(false);
      }
    };
    loadPreferences();
    return () => {
      mounted = false;
    };
  }, [shortcutMap]);

  useEffect(() => {
    if (branchId || !user || !canReadBranches) return;
    setBranchModalOpen(true);
  }, [branchId, user, canReadBranches]);

  useEffect(() => subscribeSelectedBranch((nextBranchId) => {
    setBranchId(nextBranchId);
    if (nextBranchId) setBranchModalOpen(false);
  }), []);

  useEffect(() => {
    if (!branchModalOpen) return;
    if (!canReadBranches) return;
    const fetchBranches = async () => {
      setBranchLoading(true);
      setBranchError("");
      try {
        const data = await getBranches({ pageSize: 0 });
        const normalized = normalizePaginatedResponse(data);
        const list = normalized.data || [];
        const sorted = [...list].sort((a, b) => {
          const aDate = a?.createdAt ? new Date(a.createdAt).getTime() : null;
          const bDate = b?.createdAt ? new Date(b.createdAt).getTime() : null;
          if (Number.isFinite(aDate) && Number.isFinite(bDate) && aDate !== bDate) {
            return aDate - bDate;
          }
          const aId = Number(a?.id);
          const bId = Number(b?.id);
          if (Number.isFinite(aId) && Number.isFinite(bId) && aId !== bId) {
            return aId - bId;
          }
          return String(a?.branchName || "").localeCompare(String(b?.branchName || ""));
        });
        setBranches(sorted);
      } catch (err) {
        console.error("Error fetching branches:", err);
        setBranchError("No se pudieron cargar las sucursales.");
      } finally {
        setBranchLoading(false);
      }
    };
    fetchBranches();
  }, [branchModalOpen, canReadBranches]);

  const handleSelectBranch = (nextBranchId) => {
    const nextId = String(nextBranchId);
    if (!nextId) return;
    persistSelectedBranchId(nextId);
    setBranchModalOpen(false);
  };

  const updatePinned = async (nextPinned) => {
    setPinnedIds(nextPinned);
    setSaving(true);
    try {
      await updateMyPreferences({ homeShortcuts: nextPinned });
    } catch (err) {
      console.error(err);
      setError("No se pudieron guardar los atajos.");
    } finally {
      setSaving(false);
    }
  };

  const handleTogglePin = (id) => {
    if (!id) return;
    const nextPinned = pinnedIds.includes(id)
      ? pinnedIds.filter((item) => item !== id)
      : [...pinnedIds, id];
    updatePinned(nextPinned);
  };

  const pinnedShortcuts = pinnedIds
    .map((id) => shortcutMap.get(id))
    .filter(Boolean);

  const availableShortcuts = shortcutCatalog.filter(
    (shortcut) => !pinnedIds.includes(shortcut.id)
  );

  const shortcutCategories = useMemo(() => {
    const map = new Map();
    shortcutCatalog.forEach((shortcut) => {
      if (shortcut.principalDispatchOnly && !canDispatch) return;
      const id = shortcut.sectionId || "general";
      if (!map.has(id)) {
        map.set(id, {
          id,
          label: shortcut.sectionLabel || "General",
        });
      }
    });
    return Array.from(map.values());
  }, [shortcutCatalog, canDispatch]);

  useEffect(() => {
    if (selectedShortcutCategory === "all") return;
    if (shortcutCategories.some((category) => String(category.id) === String(selectedShortcutCategory))) return;
    setSelectedShortcutCategory("all");
  }, [selectedShortcutCategory, shortcutCategories]);

  const matchesShortcutCategory = (shortcut) => (!shortcut.principalDispatchOnly || canDispatch) && (
    selectedShortcutCategory === "all"
    || String(shortcut.sectionId || "general") === String(selectedShortcutCategory)
  );

  const filteredPinnedShortcuts = pinnedShortcuts.filter(matchesShortcutCategory);
  const filteredAvailableShortcuts = availableShortcuts.filter(matchesShortcutCategory);

  const resolvePath = (shortcut) => {
    if (!shortcut) return null;
    if (typeof shortcut.path === "function") {
      return shortcut.path({ branchId });
    }
    return shortcut.path || null;
  };

  const renderShortcutCard = (shortcut, pinned) => {
    const path = resolvePath(shortcut);
    const disabled = shortcut.requiresBranch && !branchId;
    return (
      <div
        key={shortcut.id}
        className={`border rounded-lg p-4 bg-white shadow-sm flex flex-col justify-between gap-3 ${
          disabled ? "opacity-60" : ""
        }`}
      >
        <div>
          <div className="text-sm text-gray-500">Acceso rapido</div>
          <div className="text-lg font-semibold">{shortcut.label}</div>
          {disabled && (
            <div className="text-xs text-gray-500 mt-1">
              Selecciona una sucursal para usar este acceso.
            </div>
          )}
        </div>
        <div className="flex items-center justify-between gap-2">
          <button
            type="button"
            className="text-sm text-blue-600 font-semibold hover:text-blue-700"
            onClick={() => handleTogglePin(shortcut.id)}
            disabled={saving}
          >
            {pinned ? "Quitar" : "Fijar"}
          </button>
          <button
            type="button"
            className="px-3 py-2 rounded bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
            onClick={() => path && navigate(path)}
            disabled={!path || disabled}
          >
            Abrir
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {branchModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4"
          onClick={(event) => event.stopPropagation()}
        >
          <div
            className="bg-white w-full max-w-lg rounded-2xl shadow-xl border border-gray-100 p-5"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <p className="text-xs uppercase tracking-wide text-gray-500">Sucursal</p>
                <h2 className="text-lg font-semibold text-gray-900">
                  Selecciona una sucursal
                </h2>
                <p className="text-xs text-gray-500 mt-1">
                  Necesitas una sucursal activa para usar los accesos rápidos.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setBranchModalOpen(false)}
                className="text-gray-600 hover:text-gray-900 bg-white border border-gray-200 rounded-full w-9 h-9 flex items-center justify-center"
              >
                ✕
              </button>
            </div>
            {branchLoading && (
              <div className="text-sm text-gray-500">
                <InlineLoading label="Cargando sucursales" />
              </div>
            )}
            {branchError && !branchLoading && (
              <div className="text-sm text-red-600">{branchError}</div>
            )}
            {!branchLoading && !branchError && branches.length === 0 && (
              <div className="text-sm text-gray-500">
                No hay sucursales disponibles.
              </div>
            )}
            {!branchLoading && !branchError && branches.length > 0 && (
              <div className="grid grid-cols-1 gap-3 max-h-72 overflow-y-auto pr-1">
                {branches.map((branch) => (
                  <button
                    key={branch.id}
                    type="button"
                    onClick={() => handleSelectBranch(branch.id)}
                    className="border border-gray-200 rounded-lg px-4 py-3 text-left hover:border-blue-400 hover:bg-blue-50 transition"
                  >
                    <div className="text-sm font-semibold text-gray-800">
                      {branch.branchName || `Sucursal ${branch.id}`}
                    </div>
                    <div className="text-xs text-gray-500">
                      Sucursal {branch.id}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold">Inicio</h1>
          <p className="text-sm text-gray-600">
            Atajos rapidos a las secciones mas usadas.
          </p>
        </div>
        <div className="flex items-center gap-3">
          {saving && (
            <div className="text-xs text-gray-500">
              <InlineLoading label="Guardando" />
            </div>
          )}
          <Link
            to="/"
            className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700"
          >
            Portal clientes
          </Link>
        </div>
      </div>

      {error && <div className="text-sm text-red-600 mb-4">{error}</div>}

      <div className="mb-6">
        <CategoryFilterBar
          categories={shortcutCategories}
          selectedCategory={selectedShortcutCategory}
          onSelect={setSelectedShortcutCategory}
          includeAll
          allItem={{ id: "all", label: "Todos", initial: "T" }}
          emptyMessage="No hay categorias de atajos disponibles."
          isEmpty={shortcutCategories.length === 0}
        />
      </div>

      <div className="mb-8">
        <h2 className="text-lg font-semibold mb-3">Tus atajos</h2>
        {loading ? (
          <div className="text-sm text-gray-500">
            <InlineLoading />
          </div>
        ) : filteredPinnedShortcuts.length ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {filteredPinnedShortcuts.map((shortcut) => renderShortcutCard(shortcut, true))}
          </div>
        ) : (
          <div className="text-sm text-gray-500">
            {selectedShortcutCategory === "all"
              ? "Aun no tienes atajos fijados."
              : "No tienes atajos fijados en esta categoria."}
          </div>
        )}
      </div>

      <div>
        <h2 className="text-lg font-semibold mb-3">Otros accesos</h2>
        {filteredAvailableShortcuts.length ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {filteredAvailableShortcuts.map((shortcut) => renderShortcutCard(shortcut, false))}
          </div>
        ) : (
          <div className="text-sm text-gray-500">
            {selectedShortcutCategory === "all"
              ? "No hay mas accesos disponibles para tu perfil."
              : "No hay mas accesos disponibles en esta categoria."}
          </div>
        )}
      </div>
    </div>
  );
};

export default HomePage;
